/**
 * Schema Builder API — /api/v2/schemas
 *
 * Targets the v2 API base derived from VITE_API_BASE_URL (backend host in Azure).
 * Auth is handled by the shared token registered via registerTokenProvider in api.ts.
 *
 * Mock/real branching lives here — callers never import isUsingMocks.
 * Follows the same shouldUseMocks pattern as api.ts / analyticsApi.ts.
 *
 * Unmapped frontend-only fields:
 *   • `unique`   — not in the API contract; stays in local state only
 *   • `example`  — not in the API contract; stays in local state only
 *
 * data_type reconciliation (API contract ↔ frontend SchemaDataType):
 *   string         ↔ General
 *   integer        ↔ Integer
 *   number         ↔ Decimal
 *   boolean        ↔ Boolean
 *   date           ↔ Date & Time   (round-trips as "datetime" on the way back up)
 *   datetime       ↔ Date & Time
 */

import { appConfig, isUsingMocks } from "@/config/app";
import { deleteCustomSchema, getCustomSchemas, saveCustomSchema } from "@/lib/schemaBuilderStorage";
import {
  formatSchemaDate,
  getDefaultExampleForDataType,
  getDefaultFormattingForDataType,
} from "@/lib/schemaBuilderHelpers";
import {
  mockReferenceLists,
  mockReferenceListValues,
  mockRulePresets,
  mockSchemaActivity,
  mockSchemaColumns,
  mockSchemaRules,
  mockSchemas,
} from "@/mocks/schemaBuilderData";
import { authHeaders } from "@/services/api";
import type { TargetSchema } from "@/types";
import { SCHEMA_DATA_TYPES } from "@/types/schemaBuilder";
import type {
  SchemaActivityEvent,
  SchemaColumn,
  SchemaDataType,
  SchemaDetail,
  SchemaRule,
  SchemaRuleCategory,
  SchemaRulePreset,
  SchemaSummary,
  ValidationResult,
  ValidationRuleResult,
} from "@/types/schemaBuilder";

const shouldUseMocks = isUsingMocks;

function mockDelay<T>(value: T, ms = 200): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

// ── Exported extended types ───────────────────────────────────

export interface ReferenceListSummary {
  id: string;
  name: string;
  usedInColumnIds: string[];
  itemCount: number;
  values: string[];
}

export interface SchemaDetailExtended extends SchemaDetail {
  _referenceLists: ReferenceListSummary[];
  _referenceListValuesByName: Record<string, string[]>;
  warnings: string[];
}

// ── Constants ────────────────────────────────────────────────

function apiV2Url(path: string): string {
  return `${appConfig.api.v2BaseUrl}${path}`;
}

// ── Raw API shapes (snake_case, as returned by FastAPI) ──────

interface ApiSchema {
  schema_id: string;
  id: string;
  name: string;
  description: string | null;
  status: "draft" | "published" | "archived" | null;
  source: "scratch" | "template" | "file_inference" | "seeded";
  provider_id: string;
  source_schema_id: string | null;
  source_file_id: string | null;
  total_columns?: number;
  rule_count?: number;
  conflict_count?: number;
  created_at: string;
  updated_at: string;
  updated_by?: string | null;
  published_at?: string | null;
  archived_at?: string | null;
  files_remapped?: number;
  last_used_at?: string | null;
}

interface ApiColumn {
  field_id: string;
  schema_id: string;
  name: string;
  data_type: string;
  mandatory: boolean;
  field_category: string | null;
  max_length: number | null;
  definition: string | null;
  position: number;
  formatting: string | null;
}

interface ApiRuleTemplate {
  template_id: string;
  category: string | null;
  display_name: string | null;
  description: string | null;
  scope: string | null;
  applies_to_types: string[];
  parameters_schema: Record<string, unknown>;
  default_criticality: string | null;
  can_conflict: boolean;
}

interface ApiRule {
  rule_id: string;
  schema_id: string | null;
  field_id: string | null;
  scope: string | null;
  rule_template_id: string | null;
  parameters: Record<string, unknown>;
  criticality: string | null;
  rule_name: string | null;
  user_message: string | null;
  has_conflict: boolean;
  conflict_with: string[];
  conflict_reason: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface ApiReferenceListItem {
  value: string;
  position: number;
}

interface ApiReferenceList {
  reference_list_id: string;
  name: string;
  is_case_sensitive: boolean;
  source: string;
  items: ApiReferenceListItem[];
}

interface ApiSchemaDetail extends ApiSchema {
  columns: ApiColumn[];
  reference_lists: ApiReferenceList[];
  warnings: string[];
}

// ── Request body types ───────────────────────────────────────

interface CreateSchemaBody {
  name: string;
  description?: string;
  provider_id?: string;
}

interface CreateColumnBody {
  name?: string;
  data_type?: string;
  mandatory?: boolean;
  definition?: string | null;
  position?: number | null;
  formatting?: string | null;
  field_category?: string | null;
  max_length?: number | null;
}

// ── data_type mapping ────────────────────────────────────────

const API_TO_FE_TYPE: Record<string, SchemaDataType> = {
  string: "General",
  integer: "Integer",
  number: "Decimal",
  boolean: "Boolean",
  date: "Date & Time",
  datetime: "Date & Time",
};

// Derived from API_TO_FE_TYPE so the two can't drift; last entry wins when multiple
// API types share an FE type, so "Date & Time" round-trips to "datetime".
const FE_TO_API_TYPE: Record<string, string> = Object.fromEntries(
  Object.entries(API_TO_FE_TYPE).map(([api, fe]) => [fe, api])
);

// ── Mapping helpers ──────────────────────────────────────────

function mapApiColumn(api: ApiColumn): SchemaColumn {
  const dataType: SchemaDataType = API_TO_FE_TYPE[api.data_type] ?? "General";
  const sortOrder = api.position + 1;
  const example = getDefaultExampleForDataType(dataType);

  return {
    fieldId: api.field_id,
    id: String(sortOrder),
    sortOrder,
    name: api.name,
    example,
    description: api.definition ?? "",
    mandatory: api.mandatory,
    unique: false,
    dataType,
    formatting: api.formatting ?? getDefaultFormattingForDataType(dataType),
  };
}

function mapApiReferenceList(api: ApiReferenceList): ReferenceListSummary {
  const sorted = [...api.items].sort((a, b) => a.position - b.position);
  return {
    id: api.reference_list_id,
    name: api.name,
    usedInColumnIds: [],
    itemCount: api.items.length,
    values: sorted.map((i) => i.value),
  };
}

function mapApiSchema(api: ApiSchema): SchemaSummary {
  const status = api.source === "seeded" || api.status == null ? ("preset" as const) : api.status;
  return {
    id: api.id,
    name: api.name,
    description: api.description ?? "",
    status,
    totalColumns: api.total_columns ?? 0,
    rules: api.rule_count ?? 0,
    conflicts: api.conflict_count ?? 0,
    createdAt: formatSchemaDate(api.created_at),
    lastEditedAt: formatSchemaDate(api.updated_at),
    lastEditor: api.updated_by ?? "",
    publishedAt: api.published_at ? formatSchemaDate(api.published_at) : undefined,
    filesRemapped: api.files_remapped,
    lastUsedAt: api.last_used_at ? formatSchemaDate(api.last_used_at) : undefined,
    providerId: api.provider_id ?? null,
  };
}

function mapApiSchemaDetail(api: ApiSchemaDetail): SchemaDetailExtended {
  const summary = mapApiSchema(api);

  const _referenceListValuesByName: Record<string, string[]> = {};
  const _referenceLists = api.reference_lists.map((rl) => {
    const mapped = mapApiReferenceList(rl);
    _referenceListValuesByName[mapped.name] = mapped.values;
    return mapped;
  });

  const columns = [...api.columns].sort((a, b) => a.position - b.position).map(mapApiColumn);

  return {
    ...summary,
    totalColumns: columns.length,
    columns,
    mappedFiles: [],
    _referenceLists,
    _referenceListValuesByName,
    warnings: api.warnings,
  };
}

function columnToCreateBody(col: Partial<SchemaColumn>): CreateColumnBody {
  const body: CreateColumnBody = {};
  if (col.name !== undefined) body.name = col.name;
  if (col.dataType !== undefined) body.data_type = FE_TO_API_TYPE[col.dataType] ?? "string";
  if (col.mandatory !== undefined) body.mandatory = col.mandatory;
  if (col.description !== undefined) body.definition = col.description || null;
  if (col.sortOrder !== undefined) body.position = col.sortOrder - 1;
  if (col.formatting !== undefined) body.formatting = col.formatting || null;
  return body;
}

// ── Rule template mapping ────────────────────────────────────

const API_TO_FE_CATEGORY: Record<string, SchemaRuleCategory> = {
  arithmetic: "Arithmetic",
  if_then: "If-Then",
  chronological: "Chronological",
  syntactical: "Syntactical",
  mandatory: "Syntactical",
  data_type: "Syntactical",
  pattern: "Syntactical",
  reference_list: "Syntactical",
  duplicates: "Syntactical",
  summation: "Arithmetic",
  conditional: "Chronological", // default for date-based conditionals; overridden below
};

const ALL_DATA_TYPES: SchemaDataType[] = SCHEMA_DATA_TYPES;
// Per-template FE-only metadata; `category` overrides the backend mapping where it's too
// coarse (e.g. "conditional" spans 3 categories). applicableTypes omitted — backend
// applies_to_types is authoritative.
const RULE_TEMPLATE_CATALOG: Record<
  string,
  { expression?: string; category?: SchemaRuleCategory }
> = {
  mandatory: { expression: "[{col}] must not be null or empty" },
  column_unique: { expression: "[{col}] must be a unique value" },
  valid_date: { expression: "[{col}] must be a valid date" },
  valid_number: { expression: "[{col}] must be a valid number" },
  valid_integer: { expression: "[{col}] must be a whole number" },
  valid_boolean: { expression: "[{col}] must be one of: {values}" },
  max_length: {
    expression: "[{col}] must not exceed the maximum character length of {max_length}",
  },
  alphanumeric_only: { expression: "[{col}] must contain letters and digits only" },
  email_address: { expression: "[{col}] must be a valid email address" },
  uppercase_only: { expression: "[{col}] must be uppercase" },
  in_reference_list: {
    expression: "[{col}] must be a value in reference list [{reference_list_id}]",
  },
  non_negative: { expression: "[{col}] must be non-negative", category: "Arithmetic" },
  non_positive: { expression: "[{col}] must be non-positive", category: "Arithmetic" },
  value_range: { expression: "[{col}] must be between {min} and {max}", category: "Arithmetic" },
  not_in_future: { expression: "[{col}] must be on or before Present Day" },
  not_in_past: { expression: "[{col}] must be on or after Present Day" },
  must_be_in_past: { expression: "[{col}] must be strictly before Present Day" },
  before_other_date: { expression: "[{earlier_column}] must be before [{later_column}]" },
  if_x_positive_y_required: {
    expression: "If [{x_column}] > 0, [{y_column}] must not be null",
    category: "If-Then",
  },
  if_x_negative_y_required: {
    expression: "If [{x_column}] < 0, [{y_column}] must not be null",
    category: "If-Then",
  },
  subtract_equals: {
    expression: "[{z_column}] - [{y_column}] = [{x_column}] within a tolerance of {tolerance}",
  },
  sum_n_equals_total: {
    expression:
      "Values in [{total_column}] equal the sum of values in {components} within a tolerance of {tolerance}",
  },
};

function mapApiRuleTemplate(api: ApiRuleTemplate): SchemaRulePreset {
  const catalog = RULE_TEMPLATE_CATALOG[api.template_id] ?? {};

  const rawTypes: string[] = Array.isArray(api.applies_to_types) ? api.applies_to_types : [];
  const applicableDataTypes: SchemaDataType[] = rawTypes.includes("all")
    ? ALL_DATA_TYPES
    : rawTypes.map((t) => API_TO_FE_TYPE[t]).filter((t): t is SchemaDataType => Boolean(t));

  return {
    id: api.template_id,
    backendRuleType: api.template_id,
    title: api.display_name ?? api.template_id,
    category:
      catalog.category ?? API_TO_FE_CATEGORY[api.category?.toLowerCase() ?? ""] ?? "Syntactical",
    description: api.description ?? "",
    expression: catalog.expression,
    applicableDataTypes: applicableDataTypes.length > 0 ? applicableDataTypes : ALL_DATA_TYPES,
    defaultCriticality: api.default_criticality as "error" | "warn" | null,
    parametersSchema: api.parameters_schema,
    scope: api.scope ?? "column",
  };
}

function mapApiRule(
  api: ApiRule,
  templates: SchemaRulePreset[],
  columnNameById: Record<string, string> = {},
  referenceListNameById: Record<string, string> = {}
): SchemaRule {
  const template = templates.find((t) => t.id === api.rule_template_id);
  const scope = api.scope ?? template?.scope ?? (api.field_id ? "column" : "schema");
  const columnName = api.field_id ? (columnNameById[api.field_id] ?? "") : "";
  let derivedFormula = template?.expression
    ? template.expression.split("{col}").join(columnName)
    : undefined;

  // Parse parameters once — used for both formula derivation and columnIds
  const parsedParams: Record<string, unknown> = api.parameters
    ? typeof api.parameters === "string"
      ? (JSON.parse(api.parameters) as Record<string, unknown>)
      : (api.parameters as Record<string, unknown>)
    : {};

  if (derivedFormula) {
    for (const [key, val] of Object.entries(parsedParams)) {
      if (typeof val === "string") {
        const resolvedName =
          key === "reference_list_id"
            ? (referenceListNameById[val] ?? val)
            : (columnNameById[val] ?? val);
        derivedFormula = derivedFormula.split(`{${key}}`).join(resolvedName);
      } else if (typeof val === "number") {
        derivedFormula = derivedFormula.split(`{${key}}`).join(String(val));
      } else if (Array.isArray(val)) {
        const joined =
          key === "values" || key === "items"
            ? (val as string[]).map(String).join(", ")
            : (val as string[]).map((id) => `[${columnNameById[id] ?? id}]`).join(", ");
        derivedFormula = derivedFormula.split(`{${key}}`).join(joined);
      }
    }
    // If any placeholders remain unresolved, fall back to the generic description
    if (/\{[a-z_]+\}/.test(derivedFormula)) derivedFormula = undefined;
  }

  // Schema-scoped rules have null field_id — derive column IDs from parameters (which
  // store names) by inverting columnNameById.
  let columnIds: string[];
  let hasUnresolvedColumns = false;
  if (api.field_id) {
    columnIds = [api.field_id];
  } else if (scope === "schema" && Object.keys(parsedParams).length > 0) {
    const nameToId = Object.fromEntries(
      Object.entries(columnNameById).map(([id, name]) => [name, id])
    );
    const paramStrings = Object.values(parsedParams)
      .flatMap((v) => (Array.isArray(v) ? v : [v]))
      .filter((v): v is string => typeof v === "string" && v.length > 0);
    columnIds = paramStrings
      .map((name) => nameToId[name] ?? columnNameById[name]) // name→id, fallback for stored ids
      .filter((id): id is string => Boolean(id));
    columnIds = [...new Set(columnIds)];
    // If any string param didn't resolve, it references a deleted column → conflict
    hasUnresolvedColumns = paramStrings.some((name) => !nameToId[name] && !columnNameById[name]);
  } else {
    columnIds = [];
  }

  return {
    id: api.rule_id,
    title: template?.title ?? api.rule_name ?? api.rule_template_id ?? "Rule",
    category: template?.category ?? "Syntactical",
    fieldCount: columnIds.length,
    ruleKey: api.rule_name ?? api.rule_id,
    columnIds,
    formula: api.user_message || derivedFormula || template?.description || "",
    description: api.user_message ?? "",
    hasConflict: api.has_conflict || hasUnresolvedColumns,
    conflictWith: api.conflict_with,
    conflictReason: api.conflict_reason ?? undefined,
    criticality: api.criticality as "error" | "warn" | null,
    backendRuleType: api.rule_template_id ?? undefined,
    presetId: api.rule_template_id ?? undefined,
    dqType: template?.dqType,
    parameters: api.parameters,
    scope,
  };
}

// Module-level cache — templates don't change per session
let _ruleTemplatesCache: SchemaRulePreset[] | null = null;

// ── Core fetch helper ────────────────────────────────────────

interface ApiError extends Error {
  statusCode: number;
}

/**
 * Turns FastAPI / proxy error bodies into a short human-readable string.
 * Needed because validation failures often return `detail` as an array of
 * objects — without this the UI falls back to a bare "HTTP 500".
 */
function formatSchemaApiError(status: number, body: unknown): string {
  const fallback = `HTTP ${status}`;
  if (!body || typeof body !== "object") return fallback;
  const record = body as { detail?: unknown; message?: unknown };
  if (typeof record.detail === "string") return record.detail;
  if (typeof record.message === "string") return record.message;
  if (Array.isArray(record.detail)) {
    const parts = record.detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object" && "msg" in item) {
          return String((item as { msg: unknown }).msg);
        }
        return null;
      })
      .filter((part): part is string => Boolean(part));
    if (parts.length > 0) return parts.join("; ");
  }
  return fallback;
}

async function schemaFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = await authHeaders();
  const res = await fetch(apiV2Url(path), { ...options, headers });

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      message = formatSchemaApiError(res.status, await res.json());
    } catch {
      // ignore parse error
    }
    const err = new Error(message) as ApiError;
    err.statusCode = res.status;
    throw err;
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

const LIST_SCHEMAS_MAX_ATTEMPTS = 3;
const LIST_SCHEMAS_BASE_DELAY_MS = 500;

/**
 * Retries a listSchemas fetch on transient 5xx / network failures so Databricks
 * cold-start races do not surface as a permanent empty Schema Manager page.
 */
async function listSchemasWithRetry(includeArchived: boolean): Promise<SchemaSummary[]> {
  const qs = includeArchived ? "?include_archived=true" : "";
  let lastError: unknown;
  for (let attempt = 0; attempt < LIST_SCHEMAS_MAX_ATTEMPTS; attempt++) {
    try {
      const data = await schemaFetch<ApiSchema[]>(`/schemas${qs}`);
      return data.map(mapApiSchema);
    } catch (err) {
      lastError = err;
      const statusCode = (err as ApiError).statusCode;
      const retryable = statusCode === undefined || statusCode >= 500;
      const isLast = attempt >= LIST_SCHEMAS_MAX_ATTEMPTS - 1;
      if (!retryable || isLast) throw err;
      const waitMs = LIST_SCHEMAS_BASE_DELAY_MS * 2 ** attempt;
      await new Promise((resolve) => setTimeout(resolve, waitMs));
    }
  }
  throw lastError;
}

// ── Mock helpers ─────────────────────────────────────────────

const MOCK_COLUMNS_DETAIL: Pick<
  SchemaDetailExtended,
  "columns" | "_referenceLists" | "_referenceListValuesByName"
> = {
  columns: mockSchemaColumns,
  _referenceLists: mockReferenceLists,
  _referenceListValuesByName: mockReferenceListValues,
};

// ── Public API functions ─────────────────────────────────────

export async function listSchemas(includeArchived = false): Promise<SchemaSummary[]> {
  if (shouldUseMocks()) {
    const custom = getCustomSchemas();
    const customIds = new Set(custom.map((s) => s.id));
    const mocks = (mockSchemas as SchemaSummary[]).filter((s) => !customIds.has(s.id));
    return mockDelay([...custom, ...mocks]);
  }
  return listSchemasWithRetry(includeArchived);
}

/**
 * Maps Schema Builder list rows onto TargetSchema for provider assignment pickers.
 * Keeps only assignable statuses (preset / published) so draft/archived schemas
 * never appear in Add/Edit Provider schema multi-selects.
 */
export function toAssignableTargetSchemas(schemas: SchemaSummary[]): TargetSchema[] {
  return schemas
    .filter((s) => s.status === "preset" || s.status === "published")
    .map((s) => ({
      id: s.id,
      name: s.name,
      version: "",
      description: s.description,
      status: s.status === "preset" ? ("preset" as const) : ("published" as const),
      lastUsedAt: s.lastUsedAt,
    }));
}

export async function getSchemaDetail(schemaId: string): Promise<SchemaDetailExtended> {
  if (shouldUseMocks()) {
    const schema =
      (mockSchemas as SchemaSummary[]).find((s) => s.id === schemaId) ?? mockSchemas[0];
    return mockDelay({
      ...(schema as SchemaDetail),
      mappedFiles: [],
      warnings: [],
      ...MOCK_COLUMNS_DETAIL,
    });
  }
  const data = await schemaFetch<ApiSchemaDetail>(`/schemas/${schemaId}`);
  return mapApiSchemaDetail(data);
}

export async function createSchema(body: CreateSchemaBody): Promise<SchemaDetailExtended> {
  if (shouldUseMocks()) {
    return mockDelay({
      id: crypto.randomUUID(),
      name: body.name,
      description: body.description ?? "",
      status: "draft" as const,
      totalColumns: 0,
      rules: 0,
      createdAt: "Today",
      columns: [],
      mappedFiles: [],
      warnings: [],
      _referenceLists: [],
      _referenceListValuesByName: {},
    });
  }
  const data = await schemaFetch<ApiSchemaDetail>("/schemas", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return mapApiSchemaDetail(data);
}

export async function createSchemaFromTemplate(
  sourceSchemaId: string,
  body: CreateSchemaBody
): Promise<SchemaDetailExtended> {
  if (shouldUseMocks()) {
    return mockDelay({
      id: crypto.randomUUID(),
      name: body.name,
      description: body.description ?? "",
      status: "draft" as const,
      totalColumns: mockSchemaColumns.length,
      rules: 0,
      createdAt: "Today",
      mappedFiles: [],
      warnings: [],
      ...MOCK_COLUMNS_DETAIL,
    });
  }
  const data = await schemaFetch<ApiSchemaDetail>(`/schemas/from-template/${sourceSchemaId}`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return mapApiSchemaDetail(data);
}

export async function duplicateSchema(
  sourceSchemaId: string,
  newName: string,
  providerId?: string
): Promise<SchemaSummary> {
  if (shouldUseMocks()) {
    const all = [...getCustomSchemas(), ...(mockSchemas as SchemaSummary[])];
    const source = (all.find((s) => s.id === sourceSchemaId) ?? mockSchemas[0]) as SchemaSummary;
    return mockDelay({
      ...source,
      id: crypto.randomUUID(),
      name: newName,
      description: source.description ?? "",
      status: "draft" as const,
      conflicts: 0,
      lastEditedAt: "Today",
      createdAt: "Today",
    });
  }
  const data = await schemaFetch<ApiSchemaDetail>(`/schemas/from-template/${sourceSchemaId}`, {
    method: "POST",
    body: JSON.stringify({ name: newName, provider_id: providerId }),
  });
  return mapApiSchema(data);
}

export async function archiveSchema(schemaId: string): Promise<void> {
  if (shouldUseMocks()) {
    await mockDelay(undefined, 100);
    deleteCustomSchema(schemaId);
    return;
  }
  // Raw fetch, not schemaFetch: DELETE may return 200 with an empty body, which would
  // make res.json() throw and swallow the delete.
  const headers = await authHeaders();
  const res = await fetch(apiV2Url(`/schemas/${schemaId}`), { method: "DELETE", headers });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { detail?: string; message?: string };
      if (typeof body.detail === "string") message = body.detail;
      else if (typeof body.message === "string") message = body.message;
    } catch {
      // ignore parse error on error responses
    }
    const err = new Error(message) as ApiError;
    err.statusCode = res.status;
    throw err;
  }
}

export async function publishSchema(schemaId: string): Promise<void> {
  if (shouldUseMocks()) {
    await mockDelay(undefined, 100);
    return;
  }
  await schemaFetch<void>(`/schemas/${schemaId}/publish`, { method: "POST" });
}

export async function deleteDraftSchema(schemaId: string): Promise<void> {
  if (shouldUseMocks()) {
    await mockDelay(undefined, 100);
    deleteCustomSchema(schemaId);
    return;
  }
  const headers = await authHeaders();
  const res = await fetch(apiV2Url(`/schemas/${schemaId}/draft`), { method: "DELETE", headers });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { detail?: string; message?: string };
      if (typeof body.detail === "string") message = body.detail;
      else if (typeof body.message === "string") message = body.message;
    } catch {
      // ignore parse error on error responses
    }
    const err = new Error(message) as ApiError;
    err.statusCode = res.status;
    throw err;
  }
}

export async function patchSchemaMeta(
  schemaId: string,
  body: { name?: string; description?: string }
): Promise<SchemaDetailExtended> {
  if (shouldUseMocks()) {
    const all = [...getCustomSchemas(), ...(mockSchemas as SchemaSummary[])];
    const source = (all.find((s) => s.id === schemaId) ?? mockSchemas[0]) as SchemaSummary;
    return mockDelay({
      ...(source as SchemaDetail),
      ...body,
      mappedFiles: [],
      warnings: [],
      ...MOCK_COLUMNS_DETAIL,
    });
  }
  const data = await schemaFetch<ApiSchemaDetail>(`/schemas/${schemaId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  return mapApiSchemaDetail(data);
}

/** fileId must reference a file already uploaded via /api/v1/files/upload. Returns
 * warnings for inferred columns/lists with lower confidence. */
export async function createSchemaFromFile(
  fileId: string | null,
  body: { name?: string; description?: string; provider_id?: string } = {}
): Promise<SchemaDetailExtended> {
  if (shouldUseMocks()) {
    return mockDelay({
      id: crypto.randomUUID(),
      name: body.name ?? "Inferred Schema",
      description: body.description ?? "",
      status: "draft" as const,
      totalColumns: mockSchemaColumns.length,
      rules: 0,
      createdAt: "Today",
      mappedFiles: [],
      warnings: [],
      ...MOCK_COLUMNS_DETAIL,
    });
  }
  const data = await schemaFetch<ApiSchemaDetail>("/schemas/from-file", {
    method: "POST",
    body: JSON.stringify({ file_id: fileId, ...body }),
  });
  return mapApiSchemaDetail(data);
}

/** Returns the deployment-wide rule template catalogue (cached per session). */
export async function getRuleTemplates(): Promise<SchemaRulePreset[]> {
  if (shouldUseMocks()) return mockDelay(mockRulePresets);
  if (_ruleTemplatesCache) return _ruleTemplatesCache;
  try {
    // v2 returns a bare array; the old v1 manifest wrapped it as { templates: [...] } —
    // handle both defensively.
    const raw = await schemaFetch<ApiRuleTemplate[] | { templates: ApiRuleTemplate[] }>(
      "/rule-templates"
    );
    const list: ApiRuleTemplate[] = Array.isArray(raw)
      ? raw
      : ((raw as { templates?: ApiRuleTemplate[] }).templates ?? []);
    _ruleTemplatesCache = list.map(mapApiRuleTemplate);
    return _ruleTemplatesCache;
  } catch {
    return [];
  }
}

/** Pass `columnNameById` (fieldId → name) and `referenceListNameById` (reference_list_id →
 * name), both from an already-fetched schema detail, to derive column/list-specific formulas. */
export async function getSchemaRules(
  schemaId: string,
  columnNameById: Record<string, string> = {},
  referenceListNameById: Record<string, string> = {}
): Promise<SchemaRule[]> {
  if (shouldUseMocks()) return mockDelay(mockSchemaRules);
  const [apiRules, templates] = await Promise.all([
    schemaFetch<ApiRule[]>(`/schemas/${schemaId}/rules`),
    getRuleTemplates(),
  ]);
  return apiRules.map((r) => mapApiRule(r, templates, columnNameById, referenceListNameById));
}

export async function addSchemaRule(
  schemaId: string,
  rule: SchemaRule,
  columnNameById: Record<string, string> = {}
): Promise<SchemaRule> {
  if (shouldUseMocks()) return mockDelay(rule, 100);
  const templates = await getRuleTemplates();
  const templateId = rule.presetId ?? rule.backendRuleType ?? rule.id;
  const templateScope = templates.find((t) => t.id === templateId)?.scope ?? "column";
  const isSchemaScoped = templateScope === "schema";
  const body: Record<string, unknown> = {
    rule_template_id: templateId,
    scope: templateScope,
    field_id: isSchemaScoped ? null : (rule.columnIds[0] ?? null),
    parameters: rule.parameters ?? {},
  };
  if (rule.criticality !== undefined) body.criticality = rule.criticality;
  if (rule.ruleKey) body.rule_name = rule.ruleKey;
  if (rule.description) body.user_message = rule.description;
  const data = await schemaFetch<ApiRule>(`/schemas/${schemaId}/rules`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return mapApiRule(data, templates, columnNameById);
}

export async function updateSchemaRule(
  schemaId: string,
  ruleId: string,
  rule: SchemaRule,
  columnNameById: Record<string, string> = {}
): Promise<SchemaRule> {
  if (shouldUseMocks()) return mockDelay(rule, 100);
  const templates = await getRuleTemplates();
  const body: Record<string, unknown> = {};
  // Only send parameters if we have real structured ones (avoids clearing backend-set params)
  if (rule.parameters !== undefined) body.parameters = rule.parameters;
  if (rule.criticality !== undefined) body.criticality = rule.criticality;
  if (rule.ruleKey) body.rule_name = rule.ruleKey;
  if (rule.description) body.user_message = rule.description;
  const data = await schemaFetch<ApiRule>(`/schemas/${schemaId}/rules/${ruleId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  return mapApiRule(data, templates, columnNameById);
}

export async function deleteSchemaRule(schemaId: string, ruleId: string): Promise<void> {
  if (shouldUseMocks()) return mockDelay(undefined, 100);
  await schemaFetch<void>(`/schemas/${schemaId}/rules/${ruleId}`, { method: "DELETE" });
}

export async function getSchemaActivity(schemaId: string): Promise<SchemaActivityEvent[]> {
  if (shouldUseMocks()) {
    return mockDelay(mockSchemaActivity);
  }
  interface ActivityEventOut {
    activity_id: string;
    schema_id: string;
    type: string;
    actor: string | null;
    detail: string | null;
    timestamp: string | null;
  }
  const data = await schemaFetch<ActivityEventOut[]>(`/schemas/${schemaId}/activity`);
  return data.map((e) => ({
    id: e.activity_id,
    type: e.type as SchemaActivityEvent["type"],
    actor: e.actor ?? "",
    timestamp: e.timestamp ?? new Date().toISOString(),
    detail: e.detail ?? undefined,
  }));
}

export async function addColumn(schemaId: string, col: SchemaColumn): Promise<SchemaColumn> {
  if (shouldUseMocks()) {
    // Return col as-is so the caller's fieldId stays unchanged
    return mockDelay(col, 100);
  }
  const body: CreateColumnBody = {
    name: col.name || `Column_${col.sortOrder}`,
    data_type: FE_TO_API_TYPE[col.dataType] ?? "string",
    mandatory: col.mandatory,
    definition: col.description || null,
    position: col.sortOrder - 1,
    formatting: col.formatting || null,
  };
  const data = await schemaFetch<ApiColumn>(`/schemas/${schemaId}/columns`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return { ...mapApiColumn(data), unique: col.unique, example: col.example };
}

export async function editColumn(
  schemaId: string,
  fieldId: string,
  updates: Partial<SchemaColumn>
): Promise<SchemaColumn> {
  if (shouldUseMocks()) {
    return mockDelay(updates as SchemaColumn, 100);
  }
  const body = columnToCreateBody(updates);
  const data = await schemaFetch<ApiColumn>(`/schemas/${schemaId}/columns/${fieldId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  return mapApiColumn(data);
}

export async function deleteColumn(schemaId: string, fieldId: string): Promise<void> {
  if (shouldUseMocks()) {
    return mockDelay(undefined, 100);
  }
  await schemaFetch<void>(`/schemas/${schemaId}/columns/${fieldId}`, { method: "DELETE" });
}

export async function createReferenceList(
  schemaId: string,
  name: string,
  values: string[]
): Promise<{ id: string; name: string }> {
  if (shouldUseMocks()) {
    return mockDelay({ id: `mock-rl-${Date.now()}`, name }, 100);
  }
  const data = await schemaFetch<{ reference_list_id: string; name: string }>(
    `/schemas/${schemaId}/reference-lists`,
    { method: "POST", body: JSON.stringify({ name, values }) }
  );
  return { id: data.reference_list_id, name: data.name };
}

export async function updateReferenceList(
  schemaId: string,
  referenceListId: string,
  name: string,
  values: string[]
): Promise<void> {
  if (shouldUseMocks()) return mockDelay(undefined, 100);
  await schemaFetch<void>(`/schemas/${schemaId}/reference-lists/${referenceListId}`, {
    method: "PATCH",
    body: JSON.stringify({ name, values }),
  });
}

export async function deleteReferenceList(
  schemaId: string,
  referenceListId: string
): Promise<void> {
  if (shouldUseMocks()) return mockDelay(undefined, 100);
  await schemaFetch<void>(`/schemas/${schemaId}/reference-lists/${referenceListId}`, {
    method: "DELETE",
  });
}

/** Sync positions of all columns after a reorder or delete (fire-and-forget). */
export function syncColumnPositions(
  schemaId: string,
  columns: SchemaColumn[],
  excludeFieldId?: string
): void {
  if (shouldUseMocks()) return;

  for (const col of columns) {
    if (col.fieldId === excludeFieldId) continue;
    schemaFetch<ApiColumn>(`/schemas/${schemaId}/columns/${col.fieldId}`, {
      method: "PATCH",
      body: JSON.stringify({ position: col.sortOrder - 1 }),
    }).catch(() => {
      // Best-effort: position drift is acceptable; positions are re-synced on next load
    });
  }
}

// ── Save Draft (bulk) ────────────────────────────────────────

export interface SaveDraftColumnCreate {
  localId: string;
  name: string;
  dataType: SchemaDataType;
  mandatory: boolean;
  description?: string | null;
  position?: number | null;
  formatting?: string | null;
  fieldCategory?: string | null;
  maxLength?: number | null;
}

export interface SaveDraftColumnUpdate {
  fieldId: string;
  name?: string | null;
  dataType?: SchemaDataType | null;
  mandatory?: boolean | null;
  description?: string | null;
  position?: number | null;
  formatting?: string | null;
  fieldCategory?: string | null;
  maxLength?: number | null;
}

export interface SaveDraftReferenceListCreate {
  localId: string;
  name: string;
  values: string[];
}

export interface SaveDraftReferenceListUpdate {
  referenceListId: string;
  name?: string | null;
  values?: string[] | null;
}

export interface SaveDraftRuleCreate {
  localId: string;
  ruleTemplateId: string;
  scope: string;
  fieldId?: string | null;
  parameters: Record<string, unknown>;
  criticality?: string | null;
  ruleName?: string | null;
  userMessage?: string | null;
}

export interface SaveDraftRuleUpdate {
  ruleId: string;
  parameters?: Record<string, unknown> | null;
  criticality?: string | null;
  ruleName?: string | null;
  userMessage?: string | null;
}

export interface SaveDraftActivityEvent {
  type: string;
  actorEmail?: string | null;
  detail?: string | null;
  clientTimestamp: string;
}

export interface SaveDraftPayload {
  schemaId?: string | null;
  schema: { name: string; description?: string | null; providerId?: string | null };
  columns?: {
    create?: SaveDraftColumnCreate[];
    update?: SaveDraftColumnUpdate[];
    delete?: string[];
  };
  referenceLists?: {
    create?: SaveDraftReferenceListCreate[];
    update?: SaveDraftReferenceListUpdate[];
    delete?: string[];
  };
  rules?: {
    create?: SaveDraftRuleCreate[];
    update?: SaveDraftRuleUpdate[];
    delete?: string[];
  };
  activity?: SaveDraftActivityEvent[];
}

export interface SaveDraftResult {
  schemaId: string;
  columnIdMap: Record<string, string>;
  referenceListIdMap: Record<string, string>;
  ruleIdMap: Record<string, string>;
  warnings: string[];
}

interface ApiSaveDraftResult {
  schema_id: string;
  column_id_map: Record<string, string>;
  reference_list_id_map: Record<string, string>;
  rule_id_map: Record<string, string>;
  warnings: string[];
}

function toApiSaveDraftBody(payload: SaveDraftPayload): object {
  const cols = payload.columns ?? {};
  const rls = payload.referenceLists ?? {};
  const rules = payload.rules ?? {};
  return {
    schema_id: payload.schemaId ?? null,
    schema: {
      name: payload.schema.name,
      description: payload.schema.description ?? null,
      provider_id: payload.schema.providerId ?? null,
    },
    columns: {
      create: (cols.create ?? []).map((c) => ({
        local_id: c.localId,
        name: c.name,
        data_type: FE_TO_API_TYPE[c.dataType] ?? "string",
        mandatory: c.mandatory,
        definition: c.description ?? null,
        position: c.position ?? null,
        formatting: c.formatting ?? null,
        field_category: c.fieldCategory ?? null,
        max_length: c.maxLength ?? null,
      })),
      update: (cols.update ?? []).map((c) => ({
        field_id: c.fieldId,
        name: c.name ?? null,
        data_type: c.dataType != null ? (FE_TO_API_TYPE[c.dataType] ?? "string") : null,
        mandatory: c.mandatory ?? null,
        definition: c.description ?? null,
        position: c.position ?? null,
        formatting: c.formatting ?? null,
        field_category: c.fieldCategory ?? null,
        max_length: c.maxLength ?? null,
      })),
      delete: cols.delete ?? [],
    },
    reference_lists: {
      create: (rls.create ?? []).map((r) => ({
        local_id: r.localId,
        name: r.name,
        values: r.values,
      })),
      update: (rls.update ?? []).map((r) => ({
        reference_list_id: r.referenceListId,
        name: r.name ?? null,
        values: r.values ?? null,
      })),
      delete: rls.delete ?? [],
    },
    rules: {
      create: (rules.create ?? []).map((r) => ({
        local_id: r.localId,
        rule_template_id: r.ruleTemplateId,
        scope: r.scope,
        field_id: r.fieldId ?? null,
        parameters: r.parameters,
        criticality: r.criticality ?? null,
        rule_name: r.ruleName ?? null,
        user_message: r.userMessage ?? null,
      })),
      update: (rules.update ?? []).map((r) => ({
        rule_id: r.ruleId,
        parameters: r.parameters ?? null,
        criticality: r.criticality ?? null,
        rule_name: r.ruleName ?? null,
        user_message: r.userMessage ?? null,
      })),
      delete: rules.delete ?? [],
    },
    activity: (payload.activity ?? []).map((a) => ({
      type: a.type,
      actor_email: a.actorEmail ?? null,
      detail: a.detail ?? null,
      client_timestamp: a.clientTimestamp,
    })),
  };
}

export async function saveDraft(payload: SaveDraftPayload): Promise<SaveDraftResult> {
  if (shouldUseMocks()) {
    const colIdMap: Record<string, string> = {};
    for (const c of payload.columns?.create ?? []) colIdMap[c.localId] = crypto.randomUUID();
    const rlIdMap: Record<string, string> = {};
    for (const r of payload.referenceLists?.create ?? []) rlIdMap[r.localId] = crypto.randomUUID();
    const ruleIdMap: Record<string, string> = {};
    for (const r of payload.rules?.create ?? []) ruleIdMap[r.localId] = crypto.randomUUID();
    return mockDelay({
      schemaId: payload.schemaId ?? crypto.randomUUID(),
      columnIdMap: colIdMap,
      referenceListIdMap: rlIdMap,
      ruleIdMap,
      warnings: [],
    });
  }
  const data = await schemaFetch<ApiSaveDraftResult>("/schemas/save-draft", {
    method: "POST",
    body: JSON.stringify(toApiSaveDraftBody(payload)),
  });
  return {
    schemaId: data.schema_id,
    columnIdMap: data.column_id_map,
    referenceListIdMap: data.reference_list_id_map,
    ruleIdMap: data.rule_id_map,
    warnings: data.warnings,
  };
}

export interface SaveAndValidateRuleResult {
  rule_id: string;
  rule_name: string | null;
  criticality: string | null;
  status: "evaluated" | "deferred_full_run" | "skipped";
  rows_evaluated: number;
  rows_failed: number;
  failed_sample: Record<string, unknown>[];
  detail: string | null;
}

export interface SaveAndValidateResult {
  save: SaveDraftResult;
  validation: {
    passed: boolean;
    total_rows: number;
    rule_results: SaveAndValidateRuleResult[];
    unmapped_columns: string[];
    skipped_rules: string[];
  };
}

// Mock-only: fabricates a plausible validate-draft response (real API shape) from
// the save payload, so local dev without a backend still exercises the results UI.
// The first rule in `create` is made to fail so both pass/fail states are visible.
function buildMockValidation(
  payload: SaveDraftPayload,
  ruleIdMap: Record<string, string>
): SaveAndValidateResult["validation"] {
  const columnNameByLocalId = Object.fromEntries(
    (payload.columns?.create ?? []).map((c) => [c.localId, c.name])
  );
  const rowsTested = 142;
  const creates = payload.rules?.create ?? [];
  const updates = payload.rules?.update ?? [];

  const createdResults: SaveAndValidateRuleResult[] = creates.map((rule, index) => {
    const columnName = (rule.fieldId && columnNameByLocalId[rule.fieldId]) || "value";
    const shouldFail = creates.length > 1 && index === 0;
    return {
      rule_id: ruleIdMap[rule.localId] ?? rule.localId,
      rule_name: rule.ruleTemplateId,
      criticality: rule.criticality ?? null,
      status: "evaluated",
      rows_evaluated: rowsTested,
      rows_failed: shouldFail ? 3 : 0,
      failed_sample: shouldFail
        ? [{ [columnName]: "" }, { [columnName]: "-12" }, { [columnName]: "230" }]
        : [],
      detail: shouldFail ? "Mock failure for local development" : null,
    };
  });

  const updatedResults: SaveAndValidateRuleResult[] = updates.map((rule) => ({
    rule_id: rule.ruleId,
    rule_name: null,
    criticality: rule.criticality ?? null,
    status: "evaluated",
    rows_evaluated: rowsTested,
    rows_failed: 0,
    failed_sample: [],
    detail: null,
  }));

  const rule_results = [...createdResults, ...updatedResults];
  return {
    passed: rule_results.every((r) => r.rows_failed === 0),
    total_rows: rowsTested,
    rule_results,
    unmapped_columns: [],
    skipped_rules: [],
  };
}

/**
 * Save the draft, then validate it against an uploaded sample file, in one call.
 * POST /api/v2/schemas/save-and-validate (multipart).
 */
export async function saveAndValidate(
  payload: SaveDraftPayload,
  file: File,
  sampleSize?: number
): Promise<SaveAndValidateResult> {
  if (shouldUseMocks()) {
    const save = await saveDraft(payload);
    return mockDelay({ save, validation: buildMockValidation(payload, save.ruleIdMap) }, 300);
  }
  const form = new FormData();
  form.append("payload", JSON.stringify(toApiSaveDraftBody(payload)));
  form.append("file", file);
  if (sampleSize != null) form.append("sample_size", String(sampleSize));
  const headers = await authHeaders(true);
  const res = await fetch(apiV2Url("/schemas/save-and-validate"), {
    method: "POST",
    headers,
    body: form,
  });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      if (typeof body?.detail === "string") message = body.detail;
    } catch {
      // ignore parse error on error responses
    }
    const err = new Error(message) as ApiError;
    err.statusCode = res.status;
    throw err;
  }
  const data = (await res.json()) as {
    save: ApiSaveDraftResult;
    validation: SaveAndValidateResult["validation"];
  };
  return {
    save: {
      schemaId: data.save.schema_id,
      columnIdMap: data.save.column_id_map,
      referenceListIdMap: data.save.reference_list_id_map,
      ruleIdMap: data.save.rule_id_map,
      warnings: data.save.warnings,
    },
    validation: data.validation,
  };
}

/**
 * Maps the raw validate-draft response onto the shape the Validate Draft modal renders.
 * Pass the caller's already-remapped `rules`/`columns` (post-save ids), not pre-save state.
 */
export function mapValidationResult(
  validation: SaveAndValidateResult["validation"],
  context: { schemaName: string; fileName: string; rules: SchemaRule[]; columns: SchemaColumn[] }
): ValidationResult {
  const columnNameByFieldId = Object.fromEntries(context.columns.map((c) => [c.fieldId, c.name]));
  const ruleById = new Map(context.rules.map((r) => [r.id, r]));

  const ruleResults: ValidationRuleResult[] = validation.rule_results.map((r) => {
    const rule = ruleById.get(r.rule_id);
    const columnFieldId = rule?.columnIds[0];
    const columnName = columnFieldId
      ? (columnNameByFieldId[columnFieldId] ?? "Unknown column")
      : (context.columns[0]?.name ?? "Schema");
    const ruleType = rule?.title ?? r.rule_name ?? r.rule_id;

    // Rules spanning more than one column have no single meaningful "primary" one.
    const involvedColumnNames = Array.from(
      new Set(
        (rule?.columnIds ?? [])
          .map((id) => columnNameByFieldId[id])
          .filter((name): name is string => !!name)
      )
    );
    const isMultiColumn = involvedColumnNames.length > 1;

    if (r.status === "deferred_full_run" || r.status === "skipped") {
      return {
        ruleId: r.rule_id,
        columnName,
        isMultiColumn,
        ruleType,
        status: r.status,
        passed: true,
        summary:
          r.detail ??
          (r.status === "deferred_full_run"
            ? "Requires the full dataset — will run after publish, not on this sample."
            : "Skipped — its column wasn't found in the uploaded file."),
      };
    }

    const passed = r.rows_failed === 0;
    const displayColumns = involvedColumnNames.length > 0 ? involvedColumnNames : [columnName];

    const failingSample = r.failed_sample.map((row, i) => {
      const primaryValue = columnFieldId ? String(row[columnName] ?? "") : JSON.stringify(row);
      const values = columnFieldId
        ? displayColumns.map((name) => ({ column: name, value: String(row[name] ?? "") }))
        : [{ column: columnName, value: primaryValue }];
      return {
        sampleIndex: i + 1,
        values,
      };
    });

    return {
      ruleId: r.rule_id,
      columnName,
      isMultiColumn,
      ruleType,
      status: r.status,
      passed,
      summary: passed
        ? `${r.rows_evaluated} of ${r.rows_evaluated} rows passed`
        : `${r.rows_failed} of ${r.rows_evaluated} rows failed${r.detail ? ` — ${r.detail}` : ""}`,
      failingSample: failingSample.length ? failingSample : undefined,
    };
  });

  const evaluated = ruleResults.filter((r) => r.status === "evaluated");
  const passed = evaluated.filter((r) => r.passed).length;
  const failed = evaluated.length - passed;
  const skipped = ruleResults.length - evaluated.length;

  return {
    schemaName: context.schemaName,
    fileName: context.fileName,
    rowsTested: validation.total_rows,
    rulesTested: ruleResults.length,
    passed,
    failed,
    skipped,
    ruleResults,
    unmappedColumns: validation.unmapped_columns,
  };
}

/**
 * Persists a newly created schema to localStorage in mock mode.
 * In real API mode the schema was already created on backend — this is a no-op.
 */
export function finalizeNewSchema(schema: SchemaDetail): void {
  if (!shouldUseMocks()) return;
  saveCustomSchema(schema);
}
